import { useRef, useEffect, useState } from 'react';

// ─── RICH TEXT EDITOR ─────────────────────────────────────────────────────────
//
//  Lightweight contenteditable-based editor with a formatting toolbar.
//  Outputs and accepts HTML strings. Character limit is counted against plain
//  text content (not raw HTML markup).
//
//  Props:
//    value       {string}    initial HTML content
//    onChange    {function}  called with HTML string on every change
//    maxLength   {number}    max plain-text characters (default 2000)
//    placeholder {string}    placeholder text shown when empty
//

const RichTextEditor = ({
  value       = '',
  onChange,
  maxLength   = 2000,
  placeholder = 'Add a description…',
}) => {

  const editorRef = useRef(null);

  const [charCount, setCharCount] = useState(0);
  const [focused,   setFocused]   = useState(false);
  const [formats,   setFormats]   = useState({
    bold: false, italic: false, underline: false, ul: false, ol: false,
  });

  // Seed editor with initial HTML once on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
      setCharCount(getLen());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getLen = () => {
    if (!editorRef.current) return 0;
    return editorRef.current.innerText.replace(/\n$/, '').length;
  };

  const refreshFormats = () => {
    const next = {
      bold:      document.queryCommandState('bold'),
      italic:    document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      ul:        document.queryCommandState('insertUnorderedList'),
      ol:        document.queryCommandState('insertOrderedList'),
    };
    // Only re-render when a flag actually changed — otherwise every keystroke
    // / selection tick triggers a useless render that, on contenteditable,
    // can drop a drag-select mid-gesture in some browsers.
    setFormats(prev => {
      if (prev.bold === next.bold && prev.italic === next.italic
        && prev.underline === next.underline && prev.ul === next.ul && prev.ol === next.ol) {
        return prev;
      }
      return next;
    });
  };

  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    editorRef.current.focus();
    emitChange();
    refreshFormats();
  };

  // Tracks last-emitted HTML length so we can catch silent formatting drops
  // in dev mode (per spec: warn when content shrinks by >20% in one op).
  const lastEmittedLenRef = useRef(0);
  const emitChange = () => {
    setCharCount(getLen());
    const html = editorRef.current.innerHTML;
    if (import.meta.env?.DEV) {
      const prev = lastEmittedLenRef.current;
      if (prev > 0 && html.length < prev * 0.8) {
        // eslint-disable-next-line no-console
        console.warn(
          '[RichTextEditor] Description shrunk by >20% in a single change (' +
          prev + '→' + html.length + ' chars). This is usually formatting being lost — verify the read/write/sanitize pipeline.',
          { prev: prev, next: html.length, html }
        );
      }
    }
    lastEmittedLenRef.current = html.length;
    onChange(html);
  };

  // ── Key / paste handlers ────────────────────────────────────────────────────

  const handleKeyDown = (e) => {
    if (getLen() >= maxLength && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    const remaining = maxLength - getLen();
    if (remaining <= 0) return;
    document.execCommand('insertText', false, text.slice(0, remaining));
    emitChange();
  };

  // ── Derived state ───────────────────────────────────────────────────────────

  const atLimit   = charCount >= maxLength;
  const nearLimit = charCount > maxLength * 0.9;

  const toolbarButtons = [
    { key: 'bold',      cmd: 'bold',                label: 'B',  title: 'Bold',          extra: { fontWeight: 700 } },
    { key: 'italic',    cmd: 'italic',              label: 'I',  title: 'Italic',         extra: { fontStyle: 'italic' } },
    { key: 'underline', cmd: 'underline',           label: 'U',  title: 'Underline',      extra: { textDecoration: 'underline' } },
    null,
    { key: 'ul',        cmd: 'insertUnorderedList', label: '•≡', title: 'Bullet list' },
    { key: 'ol',        cmd: 'insertOrderedList',   label: '1≡', title: 'Numbered list' },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        border: `1px solid ${atLimit ? 'var(--danger)' : focused ? 'var(--accent)' : 'var(--border2)'}`,
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
        background: 'var(--bg3)',
        transition: 'border-color 0.2s',
      }}
    >
      {/* ── Toolbar ── */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 2,
          padding: '6px 8px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg2)',
        }}
      >
        {toolbarButtons.map((t, i) =>
          t === null ? (
            <div
              key={`sep-${i}`}
              aria-hidden="true"
              style={{ width: 1, height: 18, background: 'var(--border2)', margin: '0 4px', flexShrink: 0 }}
            />
          ) : (
            <button
              key={t.key}
              type="button"
              title={t.title}
              aria-label={t.title}
              aria-pressed={formats[t.key]}
              onMouseDown={e => { e.preventDefault(); exec(t.cmd); }}
              style={{
                width: 28, height: 28,
                background: formats[t.key] ? 'rgba(201,169,110,0.15)' : 'transparent',
                border: `1px solid ${formats[t.key] ? 'var(--accent)' : 'transparent'}`,
                borderRadius: 6,
                color: formats[t.key] ? 'var(--accent)' : 'var(--text2)',
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.15s',
                ...(t.extra || {}),
              }}
            >
              {t.label}
            </button>
          )
        )}
      </div>

      {/* ── Editable area ── */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Description"
        data-placeholder={placeholder}
        className="rte-content"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onInput={emitChange}
        onKeyDown={handleKeyDown}
        onKeyUp={refreshFormats}
        onMouseUp={refreshFormats}
        onPaste={handlePaste}
        onDragOver={e => e.preventDefault()}
        onDrop={e => e.preventDefault()}
        style={{
          minHeight: 80,
          padding: '10px 14px',
          fontSize: 14,
          color: 'var(--text)',
          outline: 'none',
          lineHeight: 1.6,
        }}
      />

      {/* ── Character counter (shown when approaching limit) ── */}
      {nearLimit && (
        <div style={{
          padding: '2px 14px 6px',
          fontSize: 11,
          color: atLimit ? 'var(--danger)' : 'var(--text3)',
          textAlign: 'right',
        }}>
          {charCount}/{maxLength}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
